/**
 * ConvertToInvoiceModal — Captura los datos requeridos para convertir una OC
 * en una SupplierInvoice (en estado 'registered', pendiente de aprobación
 * por contabilidad).
 */
import { todayISO } from '../../../lib/utils'
import { useMemo, useState } from 'react'

import { Autocomplete, type AutocompleteItem } from '@/components/Autocomplete'
import { ModalCancelButton } from '@/components/ModalActions'
import { Modal } from '@/components/ds/Modal'
import DatePicker from '@/components/DatePicker'
import { inputCls, labelCls } from '@/lib/formStyles'
import { useAccounts } from '@/modules/accounting/hooks/useAccounting'
import { TextField } from '@/components/ds/TextField'

interface Props {
  open: boolean
  orderNumber: string
  defaultExpenseAccountId?: string | null
  defaultExpenseAccountLabel?: string | null
  onClose: () => void
  onConfirm: (data: { invoice_number: string; issue_date: string; due_date?: string; expense_account_id?: string }) => void
  isPending?: boolean
}

export function ConvertToInvoiceModal({
  open, orderNumber,
  defaultExpenseAccountId, defaultExpenseAccountLabel,
  onClose, onConfirm, isPending = false,
}: Props) {
  const today = todayISO()
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [issueDate, setIssueDate] = useState<string>(today)
  const [dueDate, setDueDate] = useState<string>('')
  const [accountId, setAccountId] = useState<string>(defaultExpenseAccountId ?? '')

  const { data: accountsData } = useAccounts({ is_active: 'true', is_imputable: 'true' })
  const accountItems = useMemo<AutocompleteItem[]>(
    () => (accountsData?.accounts ?? [])
      .filter(a => ['expense', 'asset'].includes(a.account_type))
      .map(a => ({ id: a.id, label: `${a.code} — ${a.name}` })),
    [accountsData],
  )
  const selectedLabel = accountItems.find(i => i.id === accountId)?.label
    ?? (accountId === defaultExpenseAccountId ? defaultExpenseAccountLabel ?? '' : '')

  const handleClose = () => {
    setInvoiceNumber('')
    setIssueDate(today)
    setDueDate('')
    setAccountId(defaultExpenseAccountId ?? '')
    onClose()
  }

  const handleConfirm = () => {
    if (!invoiceNumber.trim() || !issueDate) return
    onConfirm({
      invoice_number: invoiceNumber.trim(),
      issue_date: issueDate,
      due_date: dueDate || undefined,
      expense_account_id: accountId || undefined,
    })
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`Convertir OC ${orderNumber} a factura`}
      size="sm"
      actions={
        <>
          <ModalCancelButton onClick={handleClose} />
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!invoiceNumber.trim() || !issueDate || isPending}
            className="px-4 py-2 text-sm font-medium text-on-primary bg-primary hover:bg-primary-hover rounded-lg disabled:opacity-60"
          >
            {isPending ? 'Convirtiendo…' : 'Crear factura'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-content-muted">
          La factura quedará en estado <strong>Registrada</strong>, lista para
          ser revisada y aprobada por contabilidad. El comprobante contable se
          generará al aprobar la factura.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <TextField
            dense
            className="col-span-2"
            label="Número de factura del proveedor *"
            value={invoiceNumber}
            onChange={e => setInvoiceNumber(e.target.value)}
            placeholder="Ej. 12345"
            inputProps={{ type: 'text' }}
          />
          <div>
            <label htmlFor="converttoinvoicemodal-fecha-de-emision" className={labelCls}>Fecha de emisión *</label>
            <DatePicker id="converttoinvoicemodal-fecha-de-emision" value={issueDate} onChange={setIssueDate} />
          </div>
          <div>
            <label htmlFor="converttoinvoicemodal-fecha-de-vencimiento" className={labelCls}>Fecha de vencimiento</label>
            <DatePicker id="converttoinvoicemodal-fecha-de-vencimiento" value={dueDate} onChange={setDueDate} />
          </div>
          <div className="col-span-2">
            <label htmlFor="converttoinvoicemodal-cuenta-de-gasto" className={labelCls}>Cuenta de gasto</label>
            <Autocomplete id="converttoinvoicemodal-cuenta-de-gasto"
              items={accountItems}
              value={accountId}
              displayValue={selectedLabel}
              onChange={() => { /* selección vía onSelect */ }}
              onSelect={(item: AutocompleteItem) => setAccountId(item.id)}
              placeholder="Heredar del proveedor / defaults…"
              className={inputCls}
            />
            {accountId && (
              <button
                type="button"
                onClick={() => setAccountId('')}
                className="mt-1 text-xs text-content-muted hover:text-content underline"
              >
                Limpiar
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}
